import { useState } from 'react';
import { memberSearch, matchMemberForApproval } from '@lib/care-api';
import type { MatchSuggestion, MemberCandidate } from '@lib/care-model';
import { SuggestionPill } from '@components/SuggestionPill';
import { fmtMemberId } from '@lib/member-id';
import {
  FIND_MEMBER_WORDING as W, MATCH_DECISIONS, MEMBER_SEARCH_WORDING, deriveDecision, findMemberQueries, lastFour, memberSearchUnread,
  type MatchDecision,
} from '@lib/find-member';
import { intakeIdentity, usDate } from '@lib/transcript-view';
import { useLeaveGuard } from '@lib/leave-guard';

/**
 * PART A, THE FIND MEMBER WIZARD (William, 16 Oct 2026 about 10:45 PM: "a button on the top that says "Find
 * Member" and then allows for a search of the entire member db for the client and campaign - here, we would use
 * the name and dob ... When the list comes up of members then put a checkbox next to each row and then the user
 * can tick the right box (only one) and then say match Member").
 *
 * Name and date of birth are filled from the case (the call's intake, else the caller name and date of birth
 * the voice line captured); the phone from the intake's callback number. The search is the SAME member search
 * the Search members box uses (GET /api/care/member-search): name AND date of birth as one query, the phone as
 * another, on the case's own roster, or on every campaign of the plan when widened. One row per member; ONE
 * box can be ticked (the others are held while one is ticked). Match Member queues the match and the decision
 * for approval (POST /api/care/event/match-member); nothing is written to the member before approval.
 *
 * CONSOLE FAULTS 1, 2 AND 5 (27 Oct 2026, the BCBSM opt-outs of 26 Oct):
 *   1 With `sendOnMatch` (the drawer passes MEMBERCARE_OPTOUT_EVERYWHERE), a match WITH a decision is sent to the
 *     Approval Queue in the same request (match-member send), as the queue's one-click Opt-Out and Stop are; 10 cases
 *     had been matched and left in_progress. A match with no decision stays on the case as before.
 *   2 While the match runs, leaving the page asks first (lib/leave-guard.ts).
 *   5 Match Member greyed for a missing reason says so under the button.
 */
export interface FindMemberCase {
  eventId: string;
  content?: string;
  callerName?: string;
  callerDob?: string;
  fromPhone?: string;
  suggestedAction?: string;
  claireRequestType?: string;
  requests?: ReadonlyArray<{ type?: string }>;
  actions?: ReadonlyArray<{ type?: string; data?: Record<string, unknown> | undefined }>;
}

type Row = MemberCandidate & { campaign: string | null; score?: number };

/**
 * PART R (William, 17 Oct 2026): the case's suggestion as the wizard's first rows, best first, so Find Member
 * opens on them; S1's rule decides whether the top one is pre-ticked (90+ and nobody within 10 points).
 */
export function suggestionRows(s: MatchSuggestion | undefined): { rows: Row[]; ticked: string } {
  if (!s || !s.memberId) return { rows: [], ticked: '' };
  const row = (c: { memberId: string; fullName: string; dob: string; campaign: string | null; score: number }): Row => ({
    member_id: c.memberId, full_name: c.fullName, dob: c.dob, campaign: c.campaign, score: c.score,
    insurance_id: '', language: '', kit_stage: '', pcp_name: '', phone1: '',
  });
  const rows = [row(s), ...(s.others ?? []).map(row)];
  return { rows, ticked: s.preTick ? `${s.campaign ?? ''}|${s.memberId}` : '' };
}

export function FindMemberPanel({
  ev, transcript, rosterCampaign, rosterLabel, planCampaigns, planName, campaignOffered, disabled, onQueued, onClose, suggestion, sendOnMatch,
}: {
  /** Fault 1: a match with a decision also sends the case to the Approval Queue (opt-out everywhere on). */
  sendOnMatch?: boolean;
  /** PART R: the case's stored suggestion; the wizard opens on it, the top row pre-ticked when S1 allows. */
  suggestion?: MatchSuggestion;
  ev: FindMemberCase;
  /** The call's transcript when it has been loaded, read for the intake as well as the message. */
  transcript?: string | null;
  /** The roster the case reads (its own campaign, else null = the plan's reference roster). */
  rosterCampaign: string | null;
  rosterLabel: string;
  /** Every campaign the plan has, for the widened search ([] = only this one is known). */
  planCampaigns: readonly string[];
  planName: string;
  /** Whether "Opt out of this campaign" can be applied here (MEMBERCARE_OPTOUT_EMAIL on). */
  campaignOffered: boolean;
  disabled?: boolean;
  onQueued: (message: string) => void;
  onClose: () => void;
}) {
  const fromIntake = intakeIdentity([ev.content ?? '', transcript ?? ''].join('\n'));
  const [name, setName] = useState(fromIntake.name || ev.callerName || '');
  const [dob, setDob] = useState(usDate(fromIntake.dob || ev.callerDob || ''));
  const [phone, setPhone] = useState(fromIntake.phone || '');
  const [wide, setWide] = useState(false);
  const [seed] = useState(() => suggestionRows(suggestion));
  const [rows, setRows] = useState<Row[] | null>(seed.rows.length ? seed.rows : null);
  const [unread, setUnread] = useState(false);
  const [busy, setBusy] = useState<'' | 'search' | 'match'>('');
  const [ticked, setTicked] = useState<string>(seed.ticked);
  const [decision, setDecision] = useState<MatchDecision>(() => deriveDecision(ev, campaignOffered));
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const runSearch = async () => {
    const queries = findMemberQueries({ name, dob, phone });
    if (!queries.length) {
      setError('Type a name, a date of birth or a phone first.');
      return;
    }
    setBusy('search');
    setError('');
    setTicked('');
    const campaigns: Array<string | null> = wide && planCampaigns.length ? [...planCampaigns] : [rosterCampaign];
    const seen = new Map<string, Row>();
    let anyUnread = false;
    try {
      for (const camp of campaigns) {
        for (const q of queries) {
          // The plan's own roster when the case names none (never the current-campaign list of another plan).
          const r = await memberSearch(q, 50, camp, false);
          if (memberSearchUnread(r)) anyUnread = true;
          for (const c of r.candidates) {
            const key = `${camp ?? ''}|${c.member_id}`;
            if (!seen.has(key)) seen.set(key, { ...c, campaign: camp ?? r.campaign ?? null });
          }
        }
      }
      setRows([...seen.values()]);
      setUnread(anyUnread && seen.size === 0);
    } catch {
      setRows([]);
      setUnread(true);
    } finally {
      setBusy('');
    }
  };

  const rowKey = (r: Row) => `${r.campaign ?? ''}|${r.member_id}`;
  const chosen = rows?.find((r) => rowKey(r) === ticked) ?? null;
  const needsReason = decision === 'opt-out-campaign' || decision === 'stop';
  const reasonMissing = needsReason && reason.trim().length === 0;
  const canMatch = !!chosen && !busy && !disabled && !reasonMissing && (decision !== 'opt-out-campaign' || campaignOffered);
  const sends = sendOnMatch === true && decision !== 'none';
  useLeaveGuard(busy === 'match');

  const runMatch = async () => {
    if (!chosen) return;
    setBusy('match');
    setError('');
    try {
      const r = await matchMemberForApproval(ev.eventId, { memberId: chosen.member_id, campaign: chosen.campaign, decision, reason, ...(sends ? { send: true } : {}) });
      if (!r.ok) {
        const d = (r.data ?? {}) as { error?: string; message?: string };
        setError(`Not queued (HTTP ${r.status}): ${d.message || d.error || 'the server refused it'}`);
        return;
      }
      const said = (r.data as { message?: string } | undefined)?.message;
      onQueued(said || W.pending(chosen.full_name, chosen.member_id, W.decisionWords[decision]));
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="note" data-testid="find-member-panel" style={{ marginTop: 8 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{W.head}</div>
      <div className="micro muted" style={{ marginBottom: 8 }}>{W.lede(planName)}</div>
      {suggestion?.memberId && (
        <div className="small" style={{ marginBottom: 8 }} data-testid="find-member-suggested">
          <SuggestionPill suggestion={suggestion} />{' '}
          <span className="muted">{seed.ticked ? 'Pre-ticked below; check it, then Match Member.' : 'Listed below, not ticked: check which one is right.'}</span>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label className="small" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {W.name}
          <input className="text-input" aria-label={W.name} value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="small" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {W.dob}
          <input className="text-input" aria-label={W.dob} value={dob} placeholder="5/20/1954" onChange={(e) => setDob(e.target.value)} style={{ width: 120 }} />
        </label>
        <label className="small" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {W.phone}
          <input className="text-input" aria-label={W.phone} value={phone} onChange={(e) => setPhone(e.target.value)} style={{ width: 140 }} />
        </label>
        <button type="button" className="btn btn-primary btn-sm" disabled={!!busy || disabled} onClick={() => void runSearch()}>
          {busy === 'search' ? W.searching : W.search}
        </button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>{W.cancel}</button>
      </div>
      <div className="small" style={{ display: 'flex', gap: 12, marginTop: 6, flexWrap: 'wrap' }}>
        <label><input type="radio" name="fm-scope" checked={!wide} onChange={() => setWide(false)} /> {W.scopeThis(rosterLabel)}</label>
        {planCampaigns.length > 1 && (
          <label><input type="radio" name="fm-scope" checked={wide} onChange={() => setWide(true)} /> {W.scopeAll(planName)}</label>
        )}
      </div>

      {rows !== null && (
        <div style={{ marginTop: 8, overflowX: 'auto' }}>
          {rows.length === 0 ? (
            <div className="small muted" role="status" data-testid="find-member-empty">{unread ? MEMBER_SEARCH_WORDING.unread(planName) : W.none}</div>
          ) : (
            <table className="small" data-testid="find-member-results" style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>
                  <th />
                  <th style={{ textAlign: 'left' }}>Name</th>
                  <th style={{ textAlign: 'left' }}>Member ID</th>
                  <th style={{ textAlign: 'left' }}>DOB</th>
                  <th style={{ textAlign: 'left' }}>Campaign</th>
                  <th style={{ textAlign: 'left' }}>City / State</th>
                  <th style={{ textAlign: 'left' }}>Phone</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const k = rowKey(r);
                  const on = ticked === k;
                  return (
                    <tr key={k} data-testid="find-member-row">
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`Pick ${r.full_name}`}
                          checked={on}
                          disabled={(!!ticked && !on) || !!busy}
                          title={!!ticked && !on ? W.onlyOne : undefined}
                          onChange={() => setTicked(on ? '' : k)}
                        />
                      </td>
                      <td>{r.full_name}</td>
                      <td>#{fmtMemberId(r.member_id)}</td>
                      <td>{usDate(r.dob)}</td>
                      <td>{r.campaign ?? rosterLabel}</td>
                      <td>{[r.city, r.state].filter(Boolean).join(', ')}</td>
                      <td>{lastFour(r.phone1) ? `...${lastFour(r.phone1)}` : ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {rows !== null && rows.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label className="small" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {W.decision}
            <select className="text-input" aria-label={W.decision} value={decision} onChange={(e) => setDecision(e.target.value as MatchDecision)}>
              {MATCH_DECISIONS.map((d) => (
                <option key={d} value={d} disabled={d === 'opt-out-campaign' && !campaignOffered}>{W.decisionWords[d]}</option>
              ))}
            </select>
          </label>
          {!campaignOffered && <div className="micro muted">{W.campaignOff}</div>}
          {decision !== 'none' && (
            <label className="small" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {W.reason}
              <input className="text-input" aria-label={W.reason} value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
            </label>
          )}
          <div>
            <button
              type="button"
              className="btn btn-teal btn-sm"
              disabled={!canMatch}
              title={chosen && reasonMissing ? W.reasonNeeded : undefined}
              onClick={() => void runMatch()}
            >
              {busy === 'match' ? W.matching : W.match}
            </button>
          </div>
          {chosen && reasonMissing && <div className="micro muted" data-testid="find-member-reason-needed">{W.reasonNeeded}</div>}
          {sends && <div className="micro muted" data-testid="find-member-sends-now">{W.sendsNow}</div>}
        </div>
      )}
      {error && <div className="note warn" role="alert" style={{ marginTop: 6 }}>{error}</div>}
    </div>
  );
}
